import stringHelper from "@digits-shared/helpers/stringHelper"

// Number formatting and arithmetic shared by numberHelper and the design
// system. Must stay free of GraphQL and other app dependencies.

export type NumberFormatOptions = Omit<Intl.NumberFormatOptions, "style">

export type NegativeNumberFormat = "MinusSign" | "Parentheses"

let negativeNumberFormat: NegativeNumberFormat = "MinusSign"

// Applied before the active views mount or remount when their number format changes.
export function setNegativeNumberFormat(format: NegativeNumberFormat) {
  negativeNumberFormat = format
}

export function getNegativeNumberFormat(): NegativeNumberFormat {
  return negativeNumberFormat
}

export function formatNegativeNumber(value: string): string {
  return negativeNumberFormat === "Parentheses" && value.startsWith("-")
    ? `(${value.slice(1)})`
    : value
}

export interface NumberFormatterSettings {
  negativeNumberFormat?: NegativeNumberFormat
  /**
   * Throw on option combinations Intl rejects or that would format inconsistently,
   * instead of silently normalizing them. Off by default.
   */
  throwOnInvalidOptions?: boolean
}

function invalid(message: string, throwOnInvalidOptions: boolean) {
  if (throwOnInvalidOptions) {
    throw new Error(`Invalid NumberFormatOptions, must fix: ${message}`)
  }
}

/**
 * Normalizes option combinations that Intl.NumberFormat rejects (min > max) or that
 * would format inconsistently (a maximum without a minimum).
 */
function normalizeNumberFormatOptions(
  options: NumberFormatOptions,
  { throwOnInvalidOptions = false }: NumberFormatterSettings = {}
): NumberFormatOptions {
  const {
    maximumFractionDigits,
    minimumFractionDigits,
    maximumSignificantDigits,
    minimumSignificantDigits,
    minimumIntegerDigits,
  } = options

  const newOptions = { ...options }
  if (minimumSignificantDigits === 0 || minimumIntegerDigits === 0) {
    invalid(
      `minimumSignificantDigits: ${minimumSignificantDigits}, minimumIntegerDigits: ${minimumIntegerDigits}`,
      throwOnInvalidOptions
    )
    newOptions.minimumIntegerDigits = 1
    newOptions.minimumSignificantDigits = 1
  }

  if (maximumFractionDigits !== undefined) {
    if (minimumFractionDigits === undefined || minimumFractionDigits > maximumFractionDigits) {
      invalid(
        `minimumFractionDigits: ${minimumFractionDigits}, maximumFractionDigits: ${maximumFractionDigits}`,
        throwOnInvalidOptions
      )
      newOptions.minimumFractionDigits = newOptions.maximumFractionDigits
    }
  }

  if (maximumSignificantDigits !== undefined) {
    if (
      minimumSignificantDigits === undefined ||
      minimumSignificantDigits > maximumSignificantDigits
    ) {
      invalid(
        `minimumSignificantDigits: ${minimumSignificantDigits}, maximumSignificantDigits: ${maximumSignificantDigits}`,
        throwOnInvalidOptions
      )
      newOptions.minimumSignificantDigits = newOptions.maximumSignificantDigits
    }
  }

  return newOptions
}

class FormattersCache {
  private cache: { [key: string]: Intl.NumberFormat } = {}

  getOrCreate(
    rawOptions: NumberFormatOptions,
    locale = "en-US",
    settings?: NumberFormatterSettings
  ) {
    const opts = normalizeNumberFormatOptions(rawOptions, settings)
    const key = this.cacheKey(opts, locale)
    const formatter = this.cache[key] || new Intl.NumberFormat(locale, opts)
    if (!this.cache[key]) {
      this.cache[key] = formatter
    }
    return formatter
  }

  // Only for tests
  clear() {
    this.cache = {}
  }

  private cacheKey(options: NumberFormatOptions, locale: string) {
    const keys = Object.keys(options).sort() as Array<keyof NumberFormatOptions>
    const optionsKey = keys
      .filter((key) => options[key] !== undefined)
      .map((key) => `${key}:${options[key]}`)
      .join("-")

    return `${locale}-${optionsKey}`
  }
}

const FORMATTERS = new FormattersCache()

/**
 * Returns a {@link Intl.NumberFormat} that is cached by options & locale to avoid generating
 * hundreds of instances.
 * See {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString#Performance}
 */
export function numberFormatter(
  options: NumberFormatOptions,
  locale?: string,
  settings?: NumberFormatterSettings
) {
  const formatter = FORMATTERS.getOrCreate(options, locale, settings)
  const formatToParts = (value: number | bigint) => {
    const parts = formatter.formatToParts(value)
    if (
      (settings?.negativeNumberFormat ?? negativeNumberFormat) !== "Parentheses" ||
      !parts.some((part) => part.type === "minusSign")
    )
      return parts
    return [
      { type: "literal", value: "(" },
      ...parts.filter((part) => part.type !== "minusSign"),
      { type: "literal", value: ")" },
    ] as Intl.NumberFormatPart[]
  }
  return {
    format: (value: number | bigint) =>
      (settings?.negativeNumberFormat ?? negativeNumberFormat) === "Parentheses"
        ? formatToParts(value)
            .map((part) => part.value)
            .join("")
        : formatter.format(value),
    formatToParts,
    resolvedOptions: () => formatter.resolvedOptions(),
  }
}

// Only for tests
export function clearFormattersCache() {
  FORMATTERS.clear()
}

/**
 * Converts a string into a number, supporting all basic arithmetic including operator precedence
 *
 * @param {string} raw - The string to be parsed
 * @param {boolean} abs - Whether to return the absolute value
 * @returns {number | undefined} The parsed number (to 2 decimal places) or undefined if the string is invalid
 */
export function calculate(raw: string, abs?: boolean): number | undefined {
  // strip dollar signs and commas from the raw string
  const cleaned = raw.replace(/^\(([$,\d.\s]+)\)$/, "-$1").replace(/[$,]/g, "")

  // Build an array containing the individual parts
  const parts = cleaned.match(
    // digits|operators|whitespace
    /(?:-?[\d.]+)|[-+*/]|\s+/g
  )

  // Test if everything was matched
  if (!parts || cleaned !== parts.join("")) {
    return undefined
  }

  // Remove all whitespace
  const trimmedParts = parts.map((part) => part.trim()).filter(Boolean)

  // Build a separate array containing parsed numbers
  const nums = trimmedParts.map(parseFloat)

  // Build another array with all operations reduced to additions
  const processed: number[] = []
  for (let i = 0; i < trimmedParts.length; i++) {
    const num = nums[i]
    if (num !== undefined && !isNaN(num)) {
      processed.push(num)
    } else {
      switch (trimmedParts[i]) {
        case "+":
          continue // ignore
        case "-":
          processed.push((nums[++i] || 0) * -1)
          break
        case "*":
          processed.push((processed.pop() || 0) * (nums[++i] || 0))
          break
        case "/":
          processed.push((processed.pop() || 0) / (nums[++i] || 1))
          break
        default:
          return undefined
      }
    }
  }

  // Add all numbers and return the result
  const value = processed.reduce((result, elem) => result + elem, 0)

  // Round to 2 decimal places
  // (in JS, there is no known way to do this reliably without using string parsing)
  // (https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary)
  const rounded = stringHelper.parseFloat("" + value, 2) || 0

  // Return the absolute value if abs is true
  return abs ? Math.abs(rounded) : rounded
}
